import { styled } from '@mui/material/styles';
import { Fragment } from 'react';
import ArrowRightIcon from '@/assets/nav-arrow-right.svg?react';
import FolderName from '@/components/FolderName/FolderName';
import { formatFolderName } from '@/lib/folderName';

const Breadcrumbs = ({
  path,
  onPathChange,
  onAllFilesClick,
}: {
  path: string[];
  onPathChange: (path: string[]) => void;
  onAllFilesClick?: () => void;
}) => {
  const handlePathPartClick = (idx: number) => {
    onPathChange(path.slice(0, Math.max(idx, 0)));
  };

  const totalPathLength = path.join('').length;
  const shouldCollapsePath = totalPathLength > 50 && path.length > 2;

  return (
    <BreadcrumbsContainer>
      <BreadcrumbPart
        active={path.length === 0}
        onClick={() => {
          onAllFilesClick?.();
          handlePathPartClick(0);
        }}
        title="All Files"
      >
        All Files
      </BreadcrumbPart>
      {shouldCollapsePath ? (
        <>
          <BreadcrumbArrow>
            <ArrowRightIcon />
          </BreadcrumbArrow>
          <BreadcrumbPart
            active={false}
            clickable={false}
            title={formatFolderName(path.slice(1, -2).join(' > '))}
          >
            ...
          </BreadcrumbPart>
          {path.slice(-2).map((p, idx) => {
            const actualIdx = path.length - 2 + idx;
            return (
              <Fragment key={`${p}-${actualIdx}`}>
                <BreadcrumbArrow>
                  <ArrowRightIcon />
                </BreadcrumbArrow>
                <BreadcrumbPart
                  active={actualIdx === path.length - 1}
                  onClick={() =>
                    actualIdx < path.length - 1 &&
                    handlePathPartClick(actualIdx + 1)
                  }
                  title={formatFolderName(p)}
                >
                  <FolderName name={p} />
                </BreadcrumbPart>
              </Fragment>
            );
          })}
        </>
      ) : (
        path.map((p, idx) => (
          <Fragment key={`${p}-${idx}`}>
            <BreadcrumbArrow>
              <ArrowRightIcon />
            </BreadcrumbArrow>
            <BreadcrumbPart
              active={idx === path.length - 1}
              onClick={() =>
                idx < path.length - 1 && handlePathPartClick(idx + 1)
              }
              title={formatFolderName(p)}
            >
              <FolderName name={p} />
            </BreadcrumbPart>
          </Fragment>
        ))
      )}
    </BreadcrumbsContainer>
  );
};

const BreadcrumbsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
});

const BreadcrumbPart = styled('div')<{
  active?: boolean;
  clickable?: boolean;
}>(({ active, clickable = true }) => ({
  fontSize: 14,
  fontWeight: active ? 600 : 400,
  cursor: active || !clickable ? 'default' : 'pointer',
  userSelect: 'none',
  minWidth: 40,
  maxWidth: active ? 'none' : 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: active ? 0 : 1,
}));

const BreadcrumbArrow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  margin: '0 8px',
});

export default Breadcrumbs;
